import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, Truck, ArrowRight, ChevronRight } from "lucide-react";
import { DELIVERY_ZONES, BRAND } from "@/lib/constants";
import WaitlistForm from "@/components/WaitlistForm";

// Build a flat map: zip -> { city, zone }
function useZipData(zip) {
  return useMemo(() => {
    for (const z of DELIVERY_ZONES) {
      if (z.zips.includes(zip)) return z;
    }
    return null;
  }, [zip]);
}

// Nearby ZIPs (excluding current)
function getNearby(zip) {
  const all = DELIVERY_ZONES.flatMap((z) =>
    z.zips.map((zz) => ({ zip: zz, city: z.city }))
  );
  const uniq = [];
  const seen = new Set();
  for (const item of all) {
    if (item.zip !== zip && !seen.has(item.zip)) {
      uniq.push(item);
      seen.add(item.zip);
    }
  }
  return uniq.slice(0, 6);
}

export default function AreaZip() {
  const { zip } = useParams();
  const zone = useZipData(zip);

  useEffect(() => {
    if (!zone) return;
    const title = `Cannabis Dispensary Delivery in ${zone.city} ${zip} | MD Supply Wellness`;
    const desc = `MD Supply Wellness delivers medical & adult-use cannabis to ${zone.city}, MD ${zip}. Lab-tested flower, vapes, edibles & more from our Bethesda dispensary. Join the waitlist.`;
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      "href",
      `https://mdsupplywellness.com/areas-served/${zip}`
    );
    return () => {
      document.title = "MD Supply Wellness — Bethesda, Maryland";
    };
  }, [zip, zone]);

  if (!zone) return <Navigate to="/areas-served" replace />;

  const nearby = getNearby(zip);
  const isFlagship = zone.city === "Bethesda";

  return (
    <div data-testid="area-zip-page">
      {/* HERO */}
      <section className="py-14 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14">
          {/* Breadcrumbs */}
          <nav
            aria-label="Breadcrumb"
            className="mb-8 flex items-center gap-1 text-xs text-white/50"
            data-testid="breadcrumbs"
          >
            <Link to="/" className="hover:text-[#28C0D8]">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <Link to="/areas-served" className="hover:text-[#28C0D8]">
              Areas Served
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <span className="text-white/80">
              {zone.city} {zip}
            </span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#28C0D8]/30 bg-[#28C0D8]/5 px-4 py-1.5 text-xs font-medium tracking-wide text-[#28C0D8]">
              <MapPin className="h-3 w-3" />
              {zone.city}, MD {zip}
              {isFlagship && " · Flagship area"}
            </div>
            <h1 className="mt-6 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Cannabis delivery in{" "}
              <span className="text-[#28C0D8]">
                {zone.city} {zip}.
              </span>
            </h1>
            <p className="mt-6 text-[17px] leading-relaxed text-[#94A3B8]">
              MD Supply Wellness is preparing to serve the {zone.city}{" "}
              community with a curated, lab-tested cannabis menu — flower,
              vapes, edibles, tinctures, and topicals — delivered from our
              flagship location at {BRAND.address.line1} in Bethesda,
              Maryland. Adults 21+ and registered Maryland medical patients in
              ZIP code {zip} are welcome.
            </p>

            <div className="mt-8 flex flex-col items-start sm:flex-row sm:items-center gap-3">
              <Link
                to="/menu"
                className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
              >
                Join the {zone.city} waitlist <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/5"
              >
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT BLOCKS */}
      <section className="pb-14 md:pb-24 border-t border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-3 gap-10 py-14 md:py-20">
          <div className="md:col-span-2 space-y-10">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                About {zone.city}
              </p>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                Cannabis wellness for {zone.city} residents.
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[#CBD5E1]">
                ZIP code {zip} sits at the heart of {zone.city} — a community
                that values quality, professionalism, and thoughtful service.
                Our team at MD Supply Wellness is building a dispensary
                experience worthy of that expectation: Maryland-licensed
                growers, full-panel lab testing, and unrushed, education-first
                guidance for every guest.
              </p>
              {zone.neighborhoods?.length ? (
                <>
                  <h3 className="mt-8 font-display text-lg font-semibold text-white">
                    Neighborhoods we serve in {zip}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {zone.neighborhoods.map((n) => (
                      <span
                        key={n}
                        className="inline-flex items-center rounded-full border border-white/10 bg-[#13161C] px-3 py-1 text-sm text-white/85"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </>
              ) : null}
              {zone.landmarks?.length ? (
                <p className="mt-6 text-[14px] text-[#94A3B8] leading-relaxed">
                  Convenient to {zone.landmarks.slice(0, -1).join(", ")}
                  {zone.landmarks.length > 1 ? ", and " : ""}
                  {zone.landmarks[zone.landmarks.length - 1]}.
                </p>
              ) : null}
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                What we&apos;ll deliver to {zip}
              </p>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                Curated categories, Maryland-compliant.
              </h2>
              <ul className="mt-4 space-y-3 text-[15px] text-[#CBD5E1]">
                {[
                  "Premium flower — indica, sativa, hybrid — from Maryland-licensed cultivators",
                  "Vape cartridges and disposables with lab-verified potency",
                  "Edibles and beverages including microdose options",
                  "Concentrates and rosin for experienced consumers",
                  "Tinctures and topicals for wellness-focused patients",
                ].map((line) => (
                  <li key={line} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#28C0D8] shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[14px] leading-relaxed text-[#94A3B8]">
                Every product on our menu will pass Maryland MCA testing for
                potency, pesticides, heavy metals, and microbials before it
                reaches your door in {zone.city} {zip}.
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                How delivery works
              </p>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                {zone.city} delivery, done right.
              </h2>
              <ol className="mt-4 space-y-4 text-[15px] text-[#CBD5E1]">
                <li className="flex gap-4">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#28C0D8]/15 text-[#28C0D8] text-xs font-semibold flex items-center justify-center shrink-0">
                    1
                  </span>
                  <span>
                    Browse our upcoming menu online and add your favorites to
                    the cart.
                  </span>
                </li>
                <li className="flex gap-4">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#28C0D8]/15 text-[#28C0D8] text-xs font-semibold flex items-center justify-center shrink-0">
                    2
                  </span>
                  <span>
                    Enter your {zone.city} address (must be within ZIP{" "}
                    {zip} or another served ZIP) and preferred delivery
                    window.
                  </span>
                </li>
                <li className="flex gap-4">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-[#28C0D8]/15 text-[#28C0D8] text-xs font-semibold flex items-center justify-center shrink-0">
                    3
                  </span>
                  <span>
                    A trained MD Supply driver arrives, verifies your 21+ ID
                    or medical card, and hands off your order — no lobby,
                    no rush.
                  </span>
                </li>
              </ol>
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-6">
              <Truck className="h-6 w-6 text-[#28C0D8] mb-3" />
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Serving {zip}
              </p>
              <p className="mt-2 font-display text-lg font-semibold text-white">
                {zone.city}, Maryland
              </p>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">
                Delivery from {BRAND.address.line1}, {BRAND.address.line2}.
              </p>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#28C0D8] hover:underline"
              >
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-6">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Nearby areas
              </p>
              <ul className="mt-4 space-y-2">
                {nearby.map((n) => (
                  <li key={n.zip}>
                    <Link
                      to={`/areas-served/${n.zip}`}
                      className="flex items-center justify-between text-sm text-white/85 hover:text-[#28C0D8] transition"
                    >
                      <span>
                        {n.city} <span className="text-white/40">·</span>{" "}
                        {n.zip}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/areas-served"
                className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#28C0D8] hover:underline"
              >
                All areas served <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-6">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Explore
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                <li>
                  <Link
                    to="/menu"
                    className="text-white/85 hover:text-[#28C0D8]"
                  >
                    → Our menu (coming soon)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/education"
                    className="text-white/85 hover:text-[#28C0D8]"
                  >
                    → Cannabis education hub
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-white/85 hover:text-[#28C0D8]"
                  >
                    → Frequently asked questions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/locations"
                    className="text-white/85 hover:text-[#28C0D8]"
                  >
                    → Visit our Bethesda location
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-white/85 hover:text-[#28C0D8]"
                  >
                    → Contact us
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  First in line for {zone.city}.
                </h3>
                <p className="mt-3 text-[15px] text-[#94A3B8]">
                  Join the waitlist for priority delivery to {zip} at launch.
                </p>
              </div>
              <WaitlistForm source={`area_${zip}`} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
